/**
 * The app's navigation destinations, with the permission each requires.
 *
 * Extracted from AppLayout by CMD-1 so the sidebar and the ⌘K palette read
 * one list. Two copies of this would drift the moment a destination is added
 * or a permission changes, and the palette's copy would drift silently —
 * nobody notices a command that is quietly missing.
 */
import {
  Activity, Archive, BarChart3, Briefcase, CheckSquare, ClipboardCheck, FileSignature, FileText, Hammer, HardHat, Home, Landmark, LayoutDashboard, LifeBuoy, MessageSquare, Settings, Store, TrendingUp, Upload, Users, Users2, Wallet, Wrench,
} from 'lucide-react';

// PROD-001 — Completion Roadmap sidebar IA. Marketplace stays hidden
// (routes work via direct URL). Invoices/Taxes live under Accounting;
// Trash/Imports live under Admin.
export const NAV_GROUPS = [
  {
    label: 'Daily',
    items: [
      { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, permission: 'dashboard:view' },
      { name: 'Command Center', path: '/workflow', icon: CheckSquare, permission: 'workflow:view' },
      // NI-30 — the Operator, read-only. Same key the ask and history routes gate on.
      { name: 'Northline Copilot', path: '/intelligence/ask', icon: MessageSquare, permission: 'intelligence:operate' },
    ],
  },
  {
    label: 'Assets',
    items: [
      { name: 'Deals', path: '/deals', icon: FileText, permission: 'deals:view_all' },
      { name: 'Portfolio', path: '/properties', icon: Home, permission: 'properties:view_all' },
    ],
  },
  {
    // TEN-1 — Tenants & Leasing moved out of Assets. A tenancy is something
    // the company operates day to day (collections, delinquency, renewals,
    // move-outs), not an asset it holds; the asset is the unit, and that
    // still lives in Portfolio. Lease Expirations stopped being a
    // destination of its own at the same time — it is one view of the
    // leases already on this page, and it is now a subtab there.
    label: 'Operations',
    // PORT-2 — Work Orders and Inspections become destinations of their own.
    // Both were portfolio-wide systems reachable only one property at a time:
    // maintenance lived inside a property's tab, and the Inspections hub was a
    // complete page that nothing routed to. An operator's day is "what needs
    // doing across the portfolio", not "let me open each property in turn".
    items: [
      { name: 'Tenants & Leasing', path: '/tenants', icon: Users2, permission: 'tenants_page:view' },
      { name: 'Work Orders', path: '/work-orders', icon: Wrench, permission: 'properties.maintenance:view' },
      { name: 'Inspections', path: '/inspections', icon: ClipboardCheck, permission: 'inspections:view' },
      { name: 'Construction', path: '/construction', icon: Hammer, permission: 'properties.projects:view' },
    ],
  },
  {
    // ALLOC-9 — the three rosters of people the company deals with, together.
    // Contractors moved out of Operations, Vendors arrived with the payee
    // master's first UI, and Customers came out of a dialog inside Accounts
    // Receivable. Which ledger a person touches is a fact about them, not a
    // reason to file them in three unrelated corners of the app.
    label: 'People',
    items: [
      { name: 'Contractors', path: '/contractors', icon: HardHat, permission: 'contractors:view' },
      { name: 'Vendors', path: '/vendors', icon: Store, permission: 'vendors:view' },
      { name: 'Customers', path: '/customers', icon: Users2, permission: 'accounts_receivable:view' },
      // PM-1 — the external property managers: who they are, which properties
      // they may reach, and what they may do there. Filed with the other
      // outside parties (Michael, 2026-09-26), not under Operations.
      { name: 'Property Managers', path: '/property-managers', icon: Briefcase, permission: 'property_managers:view' },
    ],
  },
  {
    // PORT-9 — Budgets and Capital Events become destinations. Both lived
    // inside a property record, which made every portfolio-wide question
    // ("which properties are behind plan?", "what did we refinance this
    // year?") a per-property hunt. A refinance is a company financial event
    // that happens TO a property, not a feature of one.
    label: 'Finance',
    items: [
      { name: 'Accounting', path: '/accounts', icon: Landmark, permission: 'accounts:view' },
      { name: 'Budgets', path: '/budgets', icon: Wallet, permission: 'properties.financials:view' },
      { name: 'Capital Events', path: '/capital-events', icon: TrendingUp, permission: 'properties.capital_events:view' },
      { name: 'Reports', path: '/reports', icon: BarChart3, permission: 'properties.reports:view' },
    ],
  },
  {
    label: 'Control',
    items: [
      { name: 'Documents', path: '/documents', icon: FileSignature, permission: 'documents:view' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { name: 'Users', path: '/users', icon: Users, permission: 'team:view' },
      { name: 'Settings', path: '/settings', icon: Settings, permission: 'settings:view' },
      { name: 'AI Operations', path: '/intelligence/operations', icon: Activity, permission: 'intelligence:configure' },
      { name: 'Imports & Data', path: '/properties/bulk-import', icon: Upload, permission: 'imports:view', alsoRequires: 'imports:financial' },
      { name: 'Trash', path: '/trash', icon: Archive, permission: 'trash:view' },
      // HELP-1 — how Northline works; for everyone, so no permission (the
      // Admin group shows for anyone who can reach one of its items).
      { name: 'Help', path: '/help', icon: LifeBuoy },
    ],
  },
];

/**
 * Can this person reach a destination? ``permission`` is required; an item
 * may name one more key in ``alsoRequires`` when every action on its page is
 * gated on both (Imports & Data: the backend's import routes all require
 * ``imports:view`` or an import action key AND ``imports:financial``). An
 * entry whose page answers every request with 403 is worse than no entry.
 */
export function canReach(item, hasPermission) {
  if (item.permission && !hasPermission(item.permission)) return false;
  if (item.alsoRequires && !hasPermission(item.alsoRequires)) return false;
  return true;
}
